import { useEffect, useState } from "react";
import useFormDraft from "../../hooks/useFormDraft";
import "./AddClient.css";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector } from "react-redux";
import {
  UilEnvelope,
  UilLock,
  UilPhone,
  UilMapMarker,
  UilBuilding,
  UilGlobe,
  UilLocationPoint,
} from "@iconscout/react-unicons";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:4000/api",
  timeout: 60000,
  headers: {
    "Content-Type": "application/json",
  },
});

export const AddClient = () => {
  const { currentUser } = useSelector((state) => state.auth);

  const [workers, setWorkers] = useState([]);
  const [tagInput, setTagInput] = useState("");

  const [formData, setFormData, clearFormDraft] = useFormDraft("add-client", {
    companyName: "",
    email: "",
    password: "",
    confirmPassword: "",
    number: "",
    siret: "",
    vatNumber: "",
    deliveryAddress: "",
    deliveryCity: "",
    deliveryPostalCode: "",
    deliveryCountry: "",
    billingSameAsDelivery: true,
    billingAddress: "",
    billingCity: "",
    billingPostalCode: "",
    billingCountry: "",
    mainMagasinName: "",
    mainMagasinContactName: "",
    mainMagasinEmail: "",
    mainMagasinNumber: "",
    mainMagasinSameAsDelivery: true,
    mainMagasinAddress: "",
    mainMagasinCity: "",
    mainMagasinPostalCode: "",
    mainMagasinCountry: "",
    mainMagasinNotes: "",
    // M3 CRM fields
    status: "active",
    source: "manual",
    assignedWorkerId: "",
    tags: [],
    internalNotes: "",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const rawToken = currentUser?.token || null;
    if (rawToken) {
      const token = rawToken.startsWith("Bearer ") ? rawToken : `Bearer ${rawToken}`;
      api.defaults.headers.common["Authorization"] = token;
    } else {
      delete api.defaults.headers.common["Authorization"];
    }
  }, [currentUser]);

  useEffect(() => {
    api.get("/workers").then((res) => setWorkers(res.data.workers || res.data || [])).catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { checked, name, type, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.companyName.trim()) {
      newErrors.companyName = "Nom de l'entreprise requis";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email requis";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email invalide";
    }

    if (!formData.password) {
      newErrors.password = "Mot de passe requis";
    } else if (formData.password.length < 6) {
      newErrors.password = "Au moins 6 caractères";
    }

    if (formData.confirmPassword && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Les mots de passe ne correspondent pas";
    }

    if (formData.siret.trim() && !/^\d{14}$/.test(formData.siret.replace(/\s/g, ""))) {
      newErrors.siret = "Le SIRET doit contenir 14 chiffres";
    }

    if (
      formData.mainMagasinEmail.trim() &&
      !/\S+@\S+\.\S+/.test(formData.mainMagasinEmail)
    ) {
      newErrors.mainMagasinEmail = "Email magasin invalide";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddTag = (e) => {
    if (e.key === "Enter" && tagInput.trim()) {
      e.preventDefault();
      const tag = tagInput.trim();
      if (!formData.tags.includes(tag)) {
        setFormData((prev) => ({ ...prev, tags: [...prev.tags, tag] }));
      }
      setTagInput("");
    }
  };

  const removeTag = (tag) => {
    setFormData((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));
  };

  const resetForm = () => {
    clearFormDraft();
    setTagInput("");
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Veuillez corriger les erreurs", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setLoading(true);

    try {
      const deliveryAddress = {
        street: formData.deliveryAddress.trim(),
        city: formData.deliveryCity.trim(),
        postalCode: formData.deliveryPostalCode.trim(),
        country: formData.deliveryCountry.trim(),
      };
      const billingAddress = formData.billingSameAsDelivery
        ? deliveryAddress
        : {
            street: formData.billingAddress.trim(),
            city: formData.billingCity.trim(),
            postalCode: formData.billingPostalCode.trim(),
            country: formData.billingCountry.trim(),
          };
      const mainMagasinAddress = formData.mainMagasinSameAsDelivery
        ? deliveryAddress
        : {
            street: formData.mainMagasinAddress.trim(),
            city: formData.mainMagasinCity.trim(),
            postalCode: formData.mainMagasinPostalCode.trim(),
            country: formData.mainMagasinCountry.trim(),
          };
      const mainMagasinId = `main-magasin-${Date.now()}`;
      const mainMagasin = {
        id: mainMagasinId,
        name: formData.mainMagasinName.trim(),
        contactName: formData.mainMagasinContactName.trim(),
        email: (formData.mainMagasinEmail || formData.email).trim().toLowerCase(),
        number: (formData.mainMagasinNumber || formData.number).trim(),
        address: mainMagasinAddress,
        notes: formData.mainMagasinNotes.trim(),
        isDefault: true,
        isActive: true,
        source: "admin",
      };
      const payload = {
        companyName: formData.companyName.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        number: formData.number.trim(),
        siret: formData.siret.replace(/\s/g, ""),
        vatNumber: formData.vatNumber.trim().toUpperCase(),
        deliveryAddress,
        billingSameAsDelivery: formData.billingSameAsDelivery,
        billingSameAsShipping: formData.billingSameAsDelivery,
        billingAddress,
        magasins: [mainMagasin],
        activeMagasinId: mainMagasinId,
        status: formData.status,
        source: formData.source,
        assignedWorkerId: formData.assignedWorkerId || undefined,
        tags: formData.tags,
        internalNotes: formData.internalNotes.trim(),
      };

      await api.post("/clients/admin/create", payload);

      toast.success("Client B2B créé avec succès !", {
        position: "top-right",
        autoClose: 3000,
      });

      resetForm();
    } catch (err) {
      const msg =
        err.response?.data?.message || "Erreur lors de la création";

      toast.error(msg, {
        position: "top-right",
        autoClose: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  const renderField = ({
    icon,
    label,
    name,
    placeholder,
    type = "text",
    required = false,
  }) => (
    <div className="form-group">
      <label>
        {icon}
        {label} {required ? "*" : ""}
      </label>
      <input
        type={type}
        name={name}
        value={formData[name]}
        onChange={handleChange}
        placeholder={placeholder}
        required={required}
        className={errors[name] ? "error" : ""}
      />
      {errors[name] && <span className="error-text">{errors[name]}</span>}
    </div>
  );

  return (
    <div className="add-client-container">
      <div className="add-client-card">

        {!currentUser && (
          <div className="auth-alert">
            Vous devez être connecté en tant qu'administrateur
          </div>
        )}

        <form onSubmit={handleSubmit} className="add-client-form">
          <section className="form-section">
            <div className="section-heading">
              <h2>Acces et entreprise</h2>
              <p>Identifiants remis au client pour acceder au B2B.</p>
            </div>

            <div className="form-grid">
              {renderField({
                icon: <UilBuilding size={16} />,
                label: "Entreprise",
                name: "companyName",
                placeholder: "Nom de l'entreprise",
                required: true,
              })}
              {renderField({
                icon: <UilEnvelope size={16} />,
                label: "Email",
                name: "email",
                placeholder: "client@entreprise.com",
                type: "email",
                required: true,
              })}
              {renderField({
                icon: <UilLock size={16} />,
                label: "Mot de passe",
                name: "password",
                placeholder: "Mot de passe temporaire",
                type: "password",
                required: true,
              })}
              {renderField({
                icon: <UilLock size={16} />,
                label: "Confirmer le mot de passe",
                name: "confirmPassword",
                placeholder: "Confirmer le mot de passe",
                type: "password",
              })}
              {renderField({
                icon: <UilPhone size={16} />,
                label: "Telephone",
                name: "number",
                placeholder: "+216 99 999 999",
                type: "tel",
              })}
              {renderField({
                icon: <UilBuilding size={16} />,
                label: "SIRET",
                name: "siret",
                placeholder: "123 456 789 00012",
              })}
              {renderField({
                icon: <UilBuilding size={16} />,
                label: "N TVA intra",
                name: "vatNumber",
                placeholder: "FR12345678901",
              })}
            </div>
          </section>

          <section className="form-section">
            <div className="section-heading">
              <h2>Adresse de livraison</h2>
              <p>Adresse proposee par defaut au moment de commander.</p>
            </div>

            <div className="form-grid">
              {renderField({
                icon: <UilMapMarker size={16} />,
                label: "Adresse",
                name: "deliveryAddress",
                placeholder: "Rue, numero, batiment",
              })}
              {renderField({
                icon: <UilLocationPoint size={16} />,
                label: "Ville",
                name: "deliveryCity",
                placeholder: "Paris",
              })}
              {renderField({
                icon: <UilLocationPoint size={16} />,
                label: "Code postal",
                name: "deliveryPostalCode",
                placeholder: "75001",
              })}
              {renderField({
                icon: <UilGlobe size={16} />,
                label: "Pays",
                name: "deliveryCountry",
                placeholder: "France",
              })}
            </div>
          </section>

          <section className="form-section">
            <div className="section-heading section-heading-row">
              <div>
                <h2>Adresse de facturation</h2>
                <p>Utilisee pour les commandes et le recapitulatif client.</p>
              </div>

              <label className="checkbox-row">
                <input
                  type="checkbox"
                  name="billingSameAsDelivery"
                  checked={formData.billingSameAsDelivery}
                  onChange={handleChange}
                />
                Identique a la livraison
              </label>
            </div>

            {!formData.billingSameAsDelivery && (
              <div className="form-grid">
                {renderField({
                  icon: <UilMapMarker size={16} />,
                  label: "Adresse",
                  name: "billingAddress",
                  placeholder: "Adresse de facturation",
                })}
                {renderField({
                  icon: <UilLocationPoint size={16} />,
                  label: "Ville",
                  name: "billingCity",
                  placeholder: "Paris",
                })}
                {renderField({
                  icon: <UilLocationPoint size={16} />,
                  label: "Code postal",
                  name: "billingPostalCode",
                  placeholder: "75001",
                })}
                {renderField({
                  icon: <UilGlobe size={16} />,
                  label: "Pays",
                  name: "billingCountry",
                  placeholder: "France",
                })}
              </div>
            )}
          </section>

          <section className="form-section">
            <div className="section-heading section-heading-row">
              <div>
                <h2>Magasin principal</h2>
                <p>Premier magasin actif du client et panier par defaut.</p>
              </div>

              <label className="checkbox-row">
                <input
                  type="checkbox"
                  name="mainMagasinSameAsDelivery"
                  checked={formData.mainMagasinSameAsDelivery}
                  onChange={handleChange}
                />
                Meme adresse que la livraison
              </label>
            </div>

            <div className="form-grid">
              {renderField({
                icon: <UilBuilding size={16} />,
                label: "Nom du magasin",
                name: "mainMagasinName",
                placeholder: "Magasin principal",
              })}
              {renderField({
                icon: <UilBuilding size={16} />,
                label: "Responsable",
                name: "mainMagasinContactName",
                placeholder: "Nom du contact",
              })}
              {renderField({
                icon: <UilEnvelope size={16} />,
                label: "Email magasin",
                name: "mainMagasinEmail",
                placeholder: "Par defaut: email client",
                type: "email",
              })}
              {renderField({
                icon: <UilPhone size={16} />,
                label: "Telephone magasin",
                name: "mainMagasinNumber",
                placeholder: "Par defaut: telephone client",
                type: "tel",
              })}

              {!formData.mainMagasinSameAsDelivery && (
                <>
                  {renderField({
                    icon: <UilMapMarker size={16} />,
                    label: "Adresse magasin",
                    name: "mainMagasinAddress",
                    placeholder: "Rue, numero, batiment",
                  })}
                  {renderField({
                    icon: <UilLocationPoint size={16} />,
                    label: "Ville magasin",
                    name: "mainMagasinCity",
                    placeholder: "Paris",
                  })}
                  {renderField({
                    icon: <UilLocationPoint size={16} />,
                    label: "Code postal magasin",
                    name: "mainMagasinPostalCode",
                    placeholder: "75001",
                  })}
                  {renderField({
                    icon: <UilGlobe size={16} />,
                    label: "Pays magasin",
                    name: "mainMagasinCountry",
                    placeholder: "France",
                  })}
                </>
              )}

              <div className="form-group form-group-full">
                <label>
                  <UilMapMarker size={16} />
                  Notes magasin
                </label>
                <textarea
                  name="mainMagasinNotes"
                  value={formData.mainMagasinNotes}
                  onChange={handleChange}
                  placeholder="Horaires, instructions livraison, contact sur place..."
                  rows="3"
                />
              </div>
            </div>
          </section>

          <div className="form-grid legacy-form-grid">
            <div className="form-group">
              <label>
                <UilBuilding size={16} />
                Entreprise *
              </label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                placeholder="Nom de l'entreprise"
                className={errors.companyName ? "error" : ""}
              />
              {errors.companyName && (
                <span className="error-text">{errors.companyName}</span>
              )}
            </div>

            <div className="form-group">
              <label>
                <UilMapMarker size={16} />
                Adresse *
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Rue, numéro, etc."
                className={errors.address ? "error" : ""}
              />
              {errors.address && (
                <span className="error-text">{errors.address}</span>
              )}
            </div>

            <div className="form-group">
              <label>
                <UilEnvelope size={16} />
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="client@entreprise.com"
                className={errors.email ? "error" : ""}
              />
              {errors.email && (
                <span className="error-text">{errors.email}</span>
              )}
            </div>

            <div className="form-group">
              <label>
                <UilLocationPoint size={16} />
                Ville *
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Tunis"
                className={errors.city ? "error" : ""}
              />
              {errors.city && (
                <span className="error-text">{errors.city}</span>
              )}
            </div>

            <div className="form-group">
              <label>
                <UilLock size={16} />
                Mot de passe *
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                className={errors.password ? "error" : ""}
              />
              {errors.password && (
                <span className="error-text">{errors.password}</span>
              )}
            </div>

            <div className="form-group">
              <label>
                <UilLocationPoint size={16} />
                Code postal *
              </label>
              <input
                type="text"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleChange}
                placeholder="1000"
                className={errors.postalCode ? "error" : ""}
              />
              {errors.postalCode && (
                <span className="error-text">{errors.postalCode}</span>
              )}
            </div>

            <div className="form-group">
              <label>
                <UilLock size={16} />
                Confirmer le mot de passe *
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="••••••••"
                className={errors.confirmPassword ? "error" : ""}
              />
              {errors.confirmPassword && (
                <span className="error-text">
                  {errors.confirmPassword}
                </span>
              )}
            </div>

            <div className="form-group">
              <label>
                <UilGlobe size={16} />
                Pays *
              </label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
                placeholder="Tunisie"
                className={errors.country ? "error" : ""}
              />
              {errors.country && (
                <span className="error-text">{errors.country}</span>
              )}
            </div>

            <div className="form-group">
              <label>
                <UilPhone size={16} />
                Téléphone *
              </label>
              <input
                type="tel"
                name="number"
                value={formData.number}
                onChange={handleChange}
                placeholder="+216 99 999 999"
                className={errors.number ? "error" : ""}
              />
              {errors.number && (
                <span className="error-text">{errors.number}</span>
              )}
            </div>

            <div className="form-group">
              <label>
                <UilBuilding size={16} />
                SIRET *
              </label>
              <input
                type="text"
                name="siret"
                value={formData.siret}
                onChange={handleChange}
                placeholder="123 456 789 00012"
                className={errors.siret ? "error" : ""}
              />
              {errors.siret && (
                <span className="error-text">{errors.siret}</span>
              )}
            </div>

            <div className="form-group">
              <label>
                <UilBuilding size={16} />
                N° TVA intra *
              </label>
              <input
                type="text"
                name="vatNumber"
                value={formData.vatNumber}
                onChange={handleChange}
                placeholder="FR12345678901"
                className={errors.vatNumber ? "error" : ""}
              />
              {errors.vatNumber && (
                <span className="error-text">{errors.vatNumber}</span>
              )}
            </div>
          </div>

          <section className="form-section">
            <div className="section-heading">
              <h2>CRM</h2>
              <p>Statut, commercial responsable et étiquettes.</p>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Statut</label>
                <select name="status" value={formData.status} onChange={handleChange}>
                  <option value="prospect">Prospect</option>
                  <option value="active">Actif</option>
                  <option value="inactive">Inactif</option>
                  <option value="blocked">Bloqué</option>
                </select>
              </div>
              <div className="form-group">
                <label>Source</label>
                <select name="source" value={formData.source} onChange={handleChange}>
                  <option value="manual">Manuel</option>
                  <option value="b2b-portal">Portail B2B</option>
                  <option value="import">Import</option>
                </select>
              </div>
              <div className="form-group">
                <label>Commercial responsable</label>
                <select name="assignedWorkerId" value={formData.assignedWorkerId} onChange={handleChange}>
                  <option value="">— Non assigné —</option>
                  {workers.map((w) => (
                    <option key={w._id} value={w._id}>{w.name} {w.surname}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Tags (Entrée pour ajouter)</label>
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleAddTag}
                  placeholder="Ex: VIP, FR, outlet..."
                />
                {formData.tags.length > 0 && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                    {formData.tags.map((t) => (
                      <span key={t} style={{ background: "#e2e8f0", borderRadius: 20, padding: "2px 10px", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
                        {t}
                        <button type="button" onClick={() => removeTag(t)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, lineHeight: 1 }}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-group form-group-full">
                <label>Notes internes</label>
                <textarea
                  name="internalNotes"
                  value={formData.internalNotes}
                  onChange={handleChange}
                  placeholder="Observations internes, contexte commercial..."
                  rows="3"
                />
              </div>
            </div>
          </section>

          <div className="submit-section">
            <button type="submit" disabled={loading} className="submit-btn">
              {loading ? "Création en cours..." : "Créer le client B2B"}
            </button>
          </div>
        </form>
      </div>

      <ToastContainer position="top-right" />
    </div>
  );
};
